import React, { useEffect, useState } from 'react';
import '../styles/CampoDataNascita.css';

/**
 * CampoDataNascita — la data di nascita scritta con i numeri (26/09/2026).
 *
 * Fino a quel giorno era un calendario: per un paziente di settant'anni
 * si tornava indietro mese per mese, e sul tablet con un dito. Una data di
 * nascita si SA, non si cerca: si scrive gg/mm/aaaa, con la tastiera
 * numerica sul tablet (inputMode="numeric") e le barre che si mettono da
 * sole. Niente calendario per le date di nascita.
 *
 * Il valore che esce e' sempre quello del database, AAAA-MM-GG, oppure ''
 * finche' la data non e' intera e possibile: chi usa il campo non deve mai
 * ricevere una data a meta' o impossibile (il 31/02 non esiste, e una data
 * nel futuro non e' una data di nascita).
 *
 * Props:
 *  - valore      'AAAA-MM-GG' (o '')
 *  - onChange    (iso) => void
 *  - className   la classe dell'input del modulo che lo ospita
 *  - grande      per il tablet
 *  - onBlur      facoltativo
 */
const daIso = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/** Le barre da sole: si tengono solo le cifre e si rimettono al loro posto. */
const conBarre = (testo) => {
    const c = String(testo).replace(/\D/g, '').slice(0, 8);
    if (c.length <= 2) return c;
    if (c.length <= 4) return `${c.slice(0, 2)}/${c.slice(2)}`;
    return `${c.slice(0, 2)}/${c.slice(2, 4)}/${c.slice(4)}`;
};

/** { iso, errore }: iso solo se la data e' intera, esiste e non e' futura. */
export function leggiDataNascita(testo, oggi = new Date()) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(testo || '');
    if (!m) return { iso: '', errore: null };
    const [g, me, a] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(Date.UTC(a, me - 1, g));
    if (me < 1 || me > 12 || d.getUTCMonth() !== me - 1 || d.getUTCDate() !== g) {
        return { iso: '', errore: 'Questa data non esiste.' };
    }
    if (a < 1900) return { iso: '', errore: 'Controlla l\'anno.' };
    const fineOggi = Date.UTC(oggi.getFullYear(), oggi.getMonth(), oggi.getDate());
    if (d.getTime() > fineOggi) return { iso: '', errore: 'La data di nascita non può essere nel futuro.' };
    return { iso: `${m[3]}-${m[2]}-${m[1]}`, errore: null };
}

const CampoDataNascita = ({ valore, onChange, className = '', grande, onBlur }) => {
    const [testo, setTesto] = useState(daIso(valore));
    const [errore, setErrore] = useState(null);

    /* Se il valore arriva da fuori (dal codice fiscale, da una bozza
       ripresa) il testo lo segue; se e' quello che il campo ha appena
       mandato, non si tocca quello che si sta scrivendo. */
    useEffect(() => {
        const suo = leggiDataNascita(testo).iso;
        if ((valore || '') !== suo) setTesto(daIso(valore));
    }, [valore]);

    const cambia = (e) => {
        const t = conBarre(e.target.value);
        setTesto(t);
        const r = leggiDataNascita(t);
        setErrore(r.errore);
        if ((r.iso || '') !== (valore || '')) onChange(r.iso);
    };

    return (
        <div className={`cdn ${grande ? 'cdn--grande' : ''}`}>
            <input
                type="text"
                inputMode="numeric"
                autoComplete="bday"
                placeholder="gg/mm/aaaa"
                maxLength={10}
                className={`${className} ${errore ? 'errore' : ''}`.trim()}
                value={testo}
                onChange={cambia}
                onBlur={onBlur}
                aria-invalid={errore ? 'true' : 'false'}
            />
            {errore && <span className="cdn-errore">{errore}</span>}
        </div>
    );
};

export default CampoDataNascita;
